import { Card, ModalBody, ModalHeader, useDisclosure } from '@nextui-org/react'
import { FC, useEffect, useState } from 'react'
import { CommonAlert } from '../components/common/Alert'
import { BaseModalFooter } from '../components/common/Modal'
import { TankModalContent } from '../components/selectTanks'
import TankSelected from '../components/selectTanks/TankSelected'
import { useSelectTankStore } from '../store/selectTank/useSelectTankStore'
import { SubmitTankArrayType } from '../types/apiResponse/tankTypes'

export type SelectTankProps = {
  submitTankArray: (selectedTanks: SubmitTankArrayType) => void
  initialTankArray?: SubmitTankArrayType | undefined
  groupIds: string[]
  limit?: number | undefined
  onClose: () => void
  instantSelect?: boolean
  availableMap?: { [tankId: string]: string[] }
  submitButtonText?: string
  singleSelect?: boolean
}

export const SelectTank: FC<SelectTankProps> = ({
  submitTankArray,
  initialTankArray,
  groupIds,
  limit,
  onClose,
  instantSelect = false,
  availableMap,
  submitButtonText = 'Select',
  singleSelect = false,
}) => {
  const { selectedTankList, initialize, close, detach } = useSelectTankStore()
  const [isSubmitActivated, setIsSubmitActivated] = useState<boolean>(false)
  const {
    isOpen: isAlertOpen,
    onOpen: onAlertOpen,
    onOpenChange: onAlertOpenChange,
  } = useDisclosure()

  useEffect(() => {
    if (selectedTankList.length !== 0) {
      if (limit !== undefined && selectedTankList.length > limit) setIsSubmitActivated(false)
      else setIsSubmitActivated(true)
    } else setIsSubmitActivated(false)
  }, [selectedTankList])

  useEffect(() => {
    if (limit !== undefined && selectedTankList.length > limit) {
      onAlertOpen()
      detach(selectedTankList[1].tankId)
    }
  }, [selectedTankList, limit, onAlertOpen])

  useEffect(() => {
    if (initialTankArray !== undefined) initialize(initialTankArray)
  }, [initialTankArray])

  const onSubmitSelectedTanks = () => {
    if (isSubmitActivated) {
      submitTankArray(selectedTankList)
      onSelectTankClose()
    }
  }

  const onSelectTankClose = () => {
    close()
    onClose()
  }

  return (
    <Card
      className="bg-white p-[0.5rem] border-1 border-opacity-60 border-tdpldarkgrey w-full max-w-full"
      data-testid="select-tank-modal-card"
    >
      <ModalHeader className="mt-[1rem] overflow-x-hidden" data-testid="select-tank-modal-header">
        <h1
          className="text-tdplindigo font-semibold text-[2rem] w-full"
          data-testid="select-tank-title"
        >
          Select Tanks
        </h1>
      </ModalHeader>
      <p
        className="text-tdplindigo text-[0.875rem] mx-7 mb-[1rem]"
        data-testid="select-tank-instructions"
      >
        Tap the tank cards below to select the tank you want to operate.
      </p>
      <ModalBody
        className="max-h-[50vh] sm:max-h-[60vh] md:max-h-[70vh] overflow-y-auto overflow-x-hidden w-full max-w-full"
        data-testid="select-tank-modal-body"
      >
        <TankModalContent
          groupIds={groupIds}
          instantSelect={instantSelect}
          onInstantSelect={(tank) => {
            submitTankArray([tank])
            onSelectTankClose()
          }}
          availableMap={availableMap}
          singleSelect={singleSelect}
        />
        {!instantSelect && <TankSelected />}
      </ModalBody>
      {!instantSelect ? (
        <BaseModalFooter
          back="Cancel"
          next={submitButtonText}
          backOnPress={onSelectTankClose}
          nextOnPress={onSubmitSelectedTanks}
          nextClassName={`${isSubmitActivated ? 'bg-tdplplum' : 'bg-tdplpigpink'} text-white`}
        />
      ) : (
        <BaseModalFooter back="Cancel" backOnPress={onSelectTankClose} />
      )}
      <CommonAlert
        isOpenAlert={isAlertOpen}
        onCloseAlert={onAlertOpenChange}
        containContinue={false}
        title="Tank Limit Over Alert"
        data-testid="tank-limit-alert"
      >
        <p data-testid="tank-limit-message">only {limit} tank(s) can be selected</p>
      </CommonAlert>
    </Card>
  )
}

export default SelectTank
